import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { useStore, Collection, CollectionRequest, HttpRequest } from '../stores';
import { createCollectionApi } from '../lib/api/collectionApi';
import { generateId } from '../lib/utils';

/**
 * Hook for managing collections and requests via the backend API.
 *
 * - Fetches collections when authenticated + workspace selected
 * - Falls back to local-only store operations when unauthenticated
 * - Provides CRUD for collections and requests
 */
export function useCollections() {
  const { config, getAccessToken, isAuthenticated } = useAuth();
  const {
    collections: storeCollections,
    setCollections,
    addCollection: storeAddCollection,
    updateCollection: storeUpdateCollection,
    deleteCollection: storeDeleteCollection,
    saveRequestToCollection,
    deleteRequest: storeDeleteRequest,
    updateRequestInCollection,
    activeWorkspaceId,
    setLocalRequestData,
    getLocalRequestData,
    openTab,
  } = useStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedWorkspaceRef = useRef<string | null>(null);

  const collections = Array.isArray(storeCollections) ? storeCollections : [];

  const api = useMemo(
    () => createCollectionApi(config, getAccessToken),
    [config, getAccessToken]
  );

  // Fetch all collections for the active workspace, including their folders/requests
  const fetchCollections = useCallback(async () => {
    if (!isAuthenticated || !activeWorkspaceId) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1. List collections for this workspace
      const collectionList = await api.listCollections(activeWorkspaceId);

      // 2. Fetch each collection's details (folders + requests)
      const fullCollections: Collection[] = await Promise.all(
        collectionList.map(async (c) => {
          try {
            const detail = await api.getCollection(c.id);
            return {
              id: detail.id,
              name: detail.name,
              description: detail.description,
              workspaceId: activeWorkspaceId,
              folders: (detail.folders || []).map((f) => ({
                id: f.id,
                name: f.name,
                parentId: f.parentId,
                requests: (detail.requests || [])
                  .filter((r) => r.folderId === f.id)
                  .map((r) => ({
                    id: r.id,
                    name: r.name,
                    method: r.method,
                    folderId: r.folderId,
                  })),
              })),
              requests: (detail.requests || [])
                .filter((r) => !r.folderId)
                .map((r) => ({
                  id: r.id,
                  name: r.name,
                  method: r.method,
                })),
            };
          } catch (err) {
            console.error(`Failed to fetch collection ${c.id}:`, err);
            // Return minimal collection on error
            return {
              id: c.id,
              name: c.name,
              description: c.description,
              workspaceId: activeWorkspaceId,
              folders: [],
              requests: [],
            };
          }
        })
      );

      // Replace the store's collections for this workspace
      // Keep collections from other workspaces intact
      const otherCollections = collections.filter(
        (c) => c.workspaceId !== activeWorkspaceId
      );
      setCollections([...otherCollections, ...fullCollections]);
      fetchedWorkspaceRef.current = activeWorkspaceId;
    } catch (err) {
      console.error('Failed to fetch collections:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch collections');
    } finally {
      setIsLoading(false);
    }
  }, [api, isAuthenticated, activeWorkspaceId, collections, setCollections]);

  // Create a collection
  const createCollection = useCallback(
    async (name: string, description?: string): Promise<Collection | null> => {
      setError(null);

      if (!isAuthenticated || !activeWorkspaceId) {
        // Local-only fallback
        const newCollection: Collection = {
          id: generateId(),
          name: name.trim(),
          description: description?.trim() || undefined,
          workspaceId: activeWorkspaceId || 'local-workspace',
          folders: [],
          requests: [],
        };
        storeAddCollection(newCollection);
        return newCollection;
      }

      try {
        const created = await api.createCollection(activeWorkspaceId, {
          name: name.trim(),
          description: description?.trim() || undefined,
        });
        const newCollection: Collection = {
          id: created.id,
          name: created.name,
          description: created.description,
          workspaceId: activeWorkspaceId,
          folders: [],
          requests: [],
        };
        storeAddCollection(newCollection);
        return newCollection;
      } catch (err) {
        console.error('Failed to create collection:', err);
        setError(err instanceof Error ? err.message : 'Failed to create collection');
        return null;
      }
    },
    [api, isAuthenticated, activeWorkspaceId, storeAddCollection]
  );

  // Update a collection
  const updateCollection = useCallback(
    async (id: string, updates: { name?: string; description?: string }): Promise<boolean> => {
      setError(null);

      if (!isAuthenticated) {
        storeUpdateCollection(id, updates);
        return true;
      }

      try {
        const updated = await api.updateCollection(id, updates);
        storeUpdateCollection(id, {
          name: updated.name,
          description: updated.description,
        });
        return true;
      } catch (err) {
        console.error('Failed to update collection:', err);
        setError(err instanceof Error ? err.message : 'Failed to update collection');
        return false;
      }
    },
    [api, isAuthenticated, storeUpdateCollection]
  );

  // Delete a collection
  const deleteCollection = useCallback(
    async (id: string): Promise<boolean> => {
      setError(null);

      if (!isAuthenticated) {
        storeDeleteCollection(id);
        return true;
      }

      try {
        await api.deleteCollection(id);
        storeDeleteCollection(id);
        return true;
      } catch (err) {
        console.error('Failed to delete collection:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete collection');
        return false;
      }
    },
    [api, isAuthenticated, storeDeleteCollection]
  );

  // Create a request in a collection
  const createRequest = useCallback(
    async (
      collectionId: string,
      request: HttpRequest,
      folderId?: string
    ): Promise<string | null> => {
      setError(null);

      if (!isAuthenticated) {
        // Local-only fallback
        const requestId = saveRequestToCollection(collectionId, request, folderId);
        return requestId;
      }

      try {
        const created = await api.createRequest(collectionId, {
          name: request.name || 'Untitled Request',
          method: request.method,
          url: request.url,
          folderId,
          headers: request.headers,
          params: request.params,
          body: request.body,
          auth: request.auth,
          scripts: request.scripts,
        });

        // Add the request reference to the collection in the store
        const collectionRequest: CollectionRequest = {
          id: created.id,
          name: created.name,
          method: created.method,
          folderId: created.folderId,
        };

        // Update the collection in the store to include this request
        const collection = collections.find((c) => c.id === collectionId);
        if (collection) {
          if (folderId) {
            storeUpdateCollection(collectionId, {
              folders: collection.folders.map((f) =>
                f.id === folderId
                  ? { ...f, requests: [...f.requests, collectionRequest] }
                  : f
              ),
            });
          } else {
            storeUpdateCollection(collectionId, {
              requests: [...collection.requests, collectionRequest],
            });
          }
        }

        // Store full request data locally for quick access
        setLocalRequestData(created.id, {
          ...request,
          id: created.id,
          name: created.name,
        });

        return created.id;
      } catch (err) {
        console.error('Failed to create request:', err);
        setError(err instanceof Error ? err.message : 'Failed to create request');
        return null;
      }
    },
    [api, isAuthenticated, collections, saveRequestToCollection, storeUpdateCollection, setLocalRequestData]
  );

  // Save/update an existing request
  const saveRequest = useCallback(
    async (requestId: string, collectionId: string, request: HttpRequest): Promise<boolean> => {
      setError(null);

      if (!isAuthenticated) {
        // Local-only fallback
        updateRequestInCollection(collectionId, requestId, request);
        return true;
      }

      try {
        await api.updateRequest(requestId, {
          name: request.name,
          method: request.method,
          url: request.url,
          headers: request.headers,
          params: request.params,
          body: request.body,
          auth: request.auth,
          scripts: request.scripts,
        });

        // Update the collection metadata in the store
        updateRequestInCollection(collectionId, requestId, request);
        return true;
      } catch (err) {
        console.error('Failed to save request:', err);
        setError(err instanceof Error ? err.message : 'Failed to save request');
        return false;
      }
    },
    [api, isAuthenticated, updateRequestInCollection]
  );

  // Delete a request
  const deleteRequest = useCallback(
    async (collectionId: string, requestId: string, folderId: string | null): Promise<boolean> => {
      setError(null);

      if (!isAuthenticated) {
        storeDeleteRequest(collectionId, requestId, folderId);
        return true;
      }

      try {
        await api.deleteRequest(requestId);
        storeDeleteRequest(collectionId, requestId, folderId);
        return true;
      } catch (err) {
        console.error('Failed to delete request:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete request');
        return false;
      }
    },
    [api, isAuthenticated, storeDeleteRequest]
  );

  // Fetch full request data from the API and open in a tab
  const fetchAndOpenRequest = useCallback(
    async (collectionId: string, request: CollectionRequest) => {
      // First check local data
      const localData = getLocalRequestData(request.id);
      if (localData) {
        openTab({
          id: request.id,
          type: 'request',
          title: localData.name,
          requestId: request.id,
          collectionId,
          request: {
            id: localData.id,
            name: localData.name,
            method: localData.method,
            url: localData.url || '',
            headers: localData.headers || [],
            params: localData.params || [],
            body: localData.body,
            auth: localData.auth,
            scripts: localData.scripts,
          },
          isDirty: false,
          isLoading: false,
        });
        return;
      }

      // Try fetching from API
      if (isAuthenticated) {
        try {
          const fullRequest = await api.getRequest(request.id);
          openTab({
            id: request.id,
            type: 'request',
            title: fullRequest.name,
            requestId: request.id,
            collectionId,
            request: {
              id: fullRequest.id,
              name: fullRequest.name,
              method: fullRequest.method as HttpRequest['method'],
              url: fullRequest.url || '',
              headers: fullRequest.headers || [],
              params: fullRequest.params || [],
              body: fullRequest.body,
              auth: fullRequest.auth as HttpRequest['auth'],
              scripts: fullRequest.scripts,
            },
            isDirty: false,
            isLoading: false,
          });
          return;
        } catch (err) {
          console.error('Failed to fetch request from API:', err);
        }
      }

      // Fallback: open with minimal data
      openTab({
        id: request.id,
        type: 'request',
        title: request.name,
        requestId: request.id,
        collectionId,
        request: {
          id: request.id,
          name: request.name,
          method: request.method as HttpRequest['method'],
          url: '',
          headers: [],
          params: [],
        },
        isDirty: false,
        isLoading: false,
      });
    },
    [api, isAuthenticated, getLocalRequestData, openTab]
  );

  // Auto-fetch when workspace changes and user is authenticated
  useEffect(() => {
    if (isAuthenticated && activeWorkspaceId && fetchedWorkspaceRef.current !== activeWorkspaceId) {
      fetchCollections();
    }
  }, [isAuthenticated, activeWorkspaceId, fetchCollections]);

  return {
    collections,
    isLoading,
    error,
    fetchCollections,
    createCollection,
    updateCollection,
    deleteCollection,
    createRequest,
    saveRequest,
    deleteRequest,
    fetchAndOpenRequest,
  };
}
